import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Redirect, Link } from "react-router-dom";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import HttpService from "../../services/httpService";
import { ROLENAME } from "../../utils/index";
import {
    makeStyles,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination,
    Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import InfoIcon from "@material-ui/icons/Info";
import Skeleton from "@material-ui/lab/Skeleton";
import moment from "moment";
import { CSVLink } from "react-csv";
import { DownloadOutlined, CarryOutOutlined, CalendarOutlined } from "@ant-design/icons";
import { Divider } from "antd";

const ALLOWED_ROLES = [ROLENAME.OperationDesignManager, ROLENAME.AGM_Design];

// Header + card styles matched to /pending-task (odmAgmPendingTask.jsx) for a consistent
// look across the ODM/AGM dashboard pages.
const useStyles = makeStyles(() => ({
    pageWrapper: {
        padding: "2rem",
        borderRadius: "10px",
        boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
        backgroundColor: "#fff",
        marginBottom: "1.5rem",
    },
    headerBanner: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "26px 20px",
        borderRadius: "16px",
        marginBottom: "20px",
        background: "linear-gradient(135deg, #1C375D 0%, #2f5583 100%)",
        boxShadow: "0 6px 18px rgba(28,55,93,0.25)",
    },
    title: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "0.3px",
        fontSize: "28px",
    },
    titleIcon: {
        fontSize: "26px",
        color: "#F47E46",
    },
    subtitle: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        marginTop: "12px",
        padding: "5px 14px",
        borderRadius: "20px",
        backgroundColor: "rgba(255,255,255,0.14)",
        color: "rgba(255,255,255,0.92)",
        fontSize: "12.5px",
        fontWeight: 600,
    },
    cardsWrapper: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "18px",
        padding: "26px",
        background: "#f0f4f8",
        borderRadius: "16px",
    },
    cardsWrapperSecondRow: {
        marginTop: "18px",
    },
    card: {
        flex: "0 0 calc(25% - 14px)",
        minHeight: "140px",
        borderRadius: "14px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "22px 16px",
        position: "relative",
        userSelect: "none",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 10px 22px rgba(0,0,0,0.16)",
        },
    },
    cardClickable: { cursor: "pointer" },
    cardOrange: { backgroundColor: "#fde8d8" },
    cardGreen:  { backgroundColor: "#d4edda" },
    cardCorner: {
        position: "absolute",
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: "solid",
        borderWidth: "0 22px 22px 0",
        borderColor: "transparent",
    },
    cardCornerOrange: { borderRightColor: "#F47E46" },
    cardCornerGreen:  { borderRightColor: "#355025" },
    cardCount: {
        fontSize: "34px",
        fontWeight: 800,
        color: "#1C375D",
        lineHeight: 1.1,
        marginBottom: "8px",
    },
    cardLabel: {
        fontSize: "13px",
        fontWeight: 700,
        color: "#1C375D",
        textAlign: "center",
        lineHeight: 1.4,
    },
    // Modal table styles
    headerCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        borderRight: "1px solid rgba(255,255,255,0.2)",
        borderBottom: "1px solid #fff",
        position: "sticky",
        top: 0,
        zIndex: 2,
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    bodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
    },
    tableRowEven: { backgroundColor: "#f9f9f9" },
    tableRowOdd:  { backgroundColor: "#fff" },
    csv: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "12px",
    },
    noData: {
        textAlign: "center",
        padding: "40px",
        color: "#94a3b8",
        fontSize: "15px",
    },
    // Dashboard table styles (matching commitmentTasks.js)
    dashHeaderCell: {
        backgroundColor: "#1C375D",
        color: "white",
        fontWeight: "bold",
        textAlign: "center",
    },
    dashTableCell: {
        textAlign: "center",
        border: "1px solid #e0e0e0",
    },
    submitButton: {
        backgroundColor: "#F47E46 !important",
        color: "white !important",
        "&:hover": { backgroundColor: "#E0663D !important" },
    },
    clearButton: {
        backgroundColor: "#f7b7b7 !important",
        color: "#151921 !important",
    },
}));

// ── Modal column definitions ───────────────────────────────────────────────────

const COLS_STANDARD = [
    { label: "Sl. No.",        key: "slno" },
    { label: "Customer Name",  key: "customerName" },
    { label: "Project No.",    key: "erpProjectNo" },
    { label: "Lead No.",       key: "lead_no" },
    { label: "Value (INR L)",  key: "value" },
    { label: "City",           key: "city" },
    { label: "Exp. Ctr. Name", key: "expCenter" },
    { label: "Current Stage",  key: "currentStage" },
    { label: "Sales Won Date", key: "salesWonDate" },
    { label: "TAT (Days)",     key: "tatDays" },
];

const COLS_IDSO = [
    { label: "Sl. No.",        key: "slno" },
    { label: "Customer Name",  key: "customerName" },
    { label: "Project No.",    key: "erpProjectNo" },
    { label: "Lead No.",       key: "lead_no" },
    { label: "Value (INR L)",  key: "value" },
    { label: "Exp. Ctr. Name", key: "expCenter" },
    { label: "Current Stage",  key: "currentStage" },
    { label: "IDSO Date",      key: "idsoDate" },
    { label: "Design User",    key: "designUser" },
    { label: "ODM",            key: "odm" },
    { label: "TAT (Days)",     key: "tatDays" },
];

const COLS_NO_ACTIVITY = [
    { label: "Sl. No.",        key: "slno" },
    { label: "Customer Name",  key: "customerName" },
    { label: "Project No.",    key: "erpProjectNo" },
    { label: "Lead No.",       key: "lead_no" },
    { label: "Value (INR L)",  key: "value" },
    { label: "City",           key: "city" },
    { label: "Exp. Ctr. Name", key: "expCenter" },
    { label: "Current Stage",  key: "currentStage" },
    { label: "Last Activity",  key: "lastActivity" },
    { label: "TAT (Days)",     key: "tatDays" },
];

function getColumns(type) {
    if (type === "idso") return COLS_IDSO;
    if (type === "noActivity") return COLS_NO_ACTIVITY;
    return COLS_STANDARD;
}

function normalizeRows(leads, type) {
    if (!Array.isArray(leads)) return [];
    return leads.map((lead, idx) => {
        const leadId = lead._id || lead.leadId || "";
        if (type === "idso") {
            return {
                slno:         idx + 1,
                leadId,
                customerName: lead.customerId?.name || lead.customerName || "-",
                erpProjectNo: lead.erpProjectNo || "-",
                lead_no:      lead.lead_no || "-",
                value:        lead.staticDesignWonBudget != null ? Number(lead.staticDesignWonBudget).toFixed(2) : "-",
                expCenter:    lead.experienceCenterName || "-",
                currentStage: lead.currentStage || "-",
                idsoDate:     lead.internalDesignSignOffDate ? moment(lead.internalDesignSignOffDate).format("DD-MM-YYYY") : "-",
                designUser:   lead.designUser?.name || "-",
                odm:          lead.designManager?.name || "-",
                tatDays:      lead.tatDays != null ? lead.tatDays : "-",
            };
        }
        if (type === "noActivity") {
            return {
                slno:         idx + 1,
                leadId,
                customerName: lead.customerId?.name || lead.customerName || "-",
                erpProjectNo: lead.erpProjectNo || "-",
                lead_no:      lead.lead_no || "-",
                value:        lead.staticSalesWonBudget != null ? Number(lead.staticSalesWonBudget).toFixed(2) : "-",
                city:         lead.cityName || "-",
                expCenter:    lead.experienceCenterName || "-",
                currentStage: lead.currentStage || "-",
                lastActivity: lead.lastActivityDate ? moment(lead.lastActivityDate).format("DD-MM-YYYY") : "No Activity",
                tatDays:      lead.tatDays != null ? lead.tatDays : "-",
            };
        }
        return {
            slno:         idx + 1,
            leadId,
            customerName: lead.customerId?.name || lead.customerName || "-",
            erpProjectNo: lead.erpProjectNo || "-",
            lead_no:      lead.lead_no || "-",
            value:        lead.staticSalesWonBudget != null ? Number(lead.staticSalesWonBudget).toFixed(2) : "-",
            city:         lead.cityName || "-",
            expCenter:    lead.experienceCenterName || "-",
            currentStage: lead.currentStage || "-",
            salesWonDate: lead.leadWonDate ? moment(lead.leadWonDate).format("DD-MM-YYYY") : "-",
            tatDays:      lead.tatDays != null ? lead.tatDays : "-",
        };
    });
}

// ── Card component ─────────────────────────────────────────────────────────────
// Cards here are all "on track" (green) — unlike /pending-task, nothing on this page
// gates the sidebar-freeze flag (that's only ever set from odmAgmPendingTask.jsx), so
// there's no orange/attention card on /my-commitments.
const ORANGE_KEYS = new Set([]);

function CommitmentCard({ classes, cardKey, count, label, onClick, loading }) {
    const isOrange = ORANGE_KEYS.has(cardKey);
    return (
        <Paper
            onClick={!loading ? onClick : undefined}
            className={`${classes.card} ${isOrange ? classes.cardOrange : classes.cardGreen} ${!loading ? classes.cardClickable : ""}`}
        >
            <span className={`${classes.cardCorner} ${isOrange ? classes.cardCornerOrange : classes.cardCornerGreen}`} />
            {loading ? (
                <Skeleton variant="rect" width={60} height={44} style={{ borderRadius: 6, marginBottom: 10, backgroundColor: "rgba(28,55,93,0.12)" }} />
            ) : (
                <Typography className={classes.cardCount}>{count}</Typography>
            )}
            <Typography className={classes.cardLabel}>
                {loading ? <Skeleton width={100} style={{ margin: "0 auto", backgroundColor: "rgba(28,55,93,0.12)" }} /> : label}
            </Typography>
        </Paper>
    );
}

// ── Constants ──────────────────────────────────────────────────────────────────
const getCurrentDate = () => new Date().toISOString().split("T")[0];
const DEFAULT_START  = "2022-01-01";

// ── Main component ─────────────────────────────────────────────────────────────

export default function OdmAgmMyCommitments() {
    const classes  = useStyles();
    const dispatch = useDispatch();
    const { userDetails } = useSelector((state) => state.user);
    const csvLinkRef = useRef(null);

    const userRoleName = userDetails?.roles?.[0]?.name || "";
    const isAllowed = ALLOWED_ROLES.includes(userRoleName);

    const [cardsLoading, setCardsLoading] = useState(true);
    const [dashData,   setDashData]   = useState(null);

    const [modal, setModal] = useState({ open: false, title: "", leads: [], type: "standard" });

    const [designFilters, setDesignFilters] = useState({
        status:    "Active + IDSO Marked Completed",
        startDate: DEFAULT_START,
        endDate:   getCurrentDate(),
        experienceCenter: "",
        city:      "",
    });
    const [pagination, setPagination] = useState({ page: 0, limit: 50, total: 0, totalPages: 0 });
    const [dashboardProjects, setDashboardProjects] = useState([]);
    const [accessibleExpCenters, setAccessibleExpCenters] = useState([]);
    const [showLatestNotes, setShowLatestNotes]   = useState(false);
    const [notesLeadData,   setNotesLeadData]     = useState({});

    useEffect(() => { fetchCards(); }, []);
    useEffect(() => {
        if (userDetails._id) fetchDesignDashboard();
    }, [userDetails._id]);

    const fetchCards = async () => {
        dispatch(setLoading(true));
        setCardsLoading(true);
        try {
            const dashRes = await HttpService.odmAgmMyCommitments();
            setDashData(dashRes.data);
        } catch (err) {
            dispatch(setSnackInfo(err?.response?.data?.message || "Something went wrong", "error"));
        } finally {
            setCardsLoading(false);
            dispatch(setLoading(false));
        }
    };

    const fetchDesignDashboard = async (
        filters   = designFilters,
        page      = pagination.page,
        limit     = pagination.limit
    ) => {
        dispatch(setLoading(true));
        try {
            const res = await HttpService.odmDesignProjectsDashboard({
                status:           filters.status || "Active + IDSO Marked Completed",
                startDate:        filters.startDate || DEFAULT_START,
                endDate:          filters.endDate   || getCurrentDate(),
                experienceCenter: filters.experienceCenter || "",
                city:             filters.city      || "",
                page:             page + 1,
                limit,
            });
            if (res.data?.success) {
                setDashboardProjects(res.data.data || []);
                if (res.data.accessibleExpCenters?.length && !accessibleExpCenters.length) {
                    setAccessibleExpCenters(res.data.accessibleExpCenters);
                }
                if (res.data.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total:      res.data.pagination.totalItems,
                        totalPages: res.data.pagination.totalPages,
                    }));
                }
            }
        } catch (err) {
            dispatch(setSnackInfo(err?.response?.data?.message || "Failed to fetch design projects", "error"));
        }
        dispatch(setLoading(false));
    };

    const handleFilterChange = (name, value) =>
        setDesignFilters(prev => ({ ...prev, [name]: value }));

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, page: 0 }));
        fetchDesignDashboard(designFilters, 0);
    };

    const clearFilters = () => {
        const def = { status: "Active + IDSO Marked Completed", startDate: DEFAULT_START, endDate: getCurrentDate(), experienceCenter: "", city: "" };
        setDesignFilters(def);
        setPagination(prev => ({ ...prev, page: 0 }));
        fetchDesignDashboard(def, 0);
    };

    const handlePageChange = (_, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
        fetchDesignDashboard(designFilters, newPage);
    };

    const handleRowsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setPagination(prev => ({ ...prev, limit: newLimit, page: 0 }));
        fetchDesignDashboard(designFilters, 0, newLimit);
    };

    const availableCities = [...new Set(accessibleExpCenters.map(ec => ec.city).filter(Boolean))];

    if (userDetails?._id && !isAllowed) return <Redirect to="/unauthorized" />;

    const openModal  = (title, leads, type = "standard") => setModal({ open: true, title, leads, type });
    const closeModal = () => setModal({ open: false, title: "", leads: [], type: "standard" });

    const columns    = getColumns(modal.type);
    const rows       = normalizeRows(modal.leads, modal.type);
    const csvHeaders = columns.map(c => ({ label: c.label, key: c.key }));

    // First row: the 3 top-level dashboard summaries. Second row: the 4 design-stage
    // commitment counts (Design Discussions / SQC / Material Selection / Design
    // Finalization), kept together on their own line.
    const topRowCards = [
        { cardKey: "designHealthM", label: "Monthly Design Health Summary", count: dashData?.designHealthM?.count ?? 0, onClick: () => openModal("Monthly Design Health Summary — M Column", dashData?.designHealthM?.data || [], "standard") },
        { cardKey: "idso",          label: "IDSO Done",                    count: dashData?.idsoDone?.count      ?? 0, onClick: () => openModal("IDSO Done", dashData?.idsoDone?.data || [], "idso") },
        { cardKey: "noActivity",    label: "No Deal Activity >5 Days",     count: dashData?.noActivity?.count    ?? 0, onClick: () => openModal("No Deal Activity >5 Days", dashData?.noActivity?.data || [], "noActivity") },
    ];
    const stageRowCards = [
        { cardKey: "designDiscussion",   label: "Design Discussions",  count: dashData?.designDiscussion?.count   ?? 0, onClick: () => openModal("Design Discussions",          dashData?.designDiscussion?.data   || [], "standard") },
        { cardKey: "sqc",                label: "SQC",                 count: dashData?.sqc?.count                ?? 0, onClick: () => openModal("Site QC / Project Kick-Off",  dashData?.sqc?.data                || [], "standard") },
        { cardKey: "materialSelection",  label: "Material Selection",  count: dashData?.materialSelection?.count  ?? 0, onClick: () => openModal("Finalization of Materials",   dashData?.materialSelection?.data  || [], "standard") },
        { cardKey: "designFinalization", label: "Design Finalization", count: dashData?.designFinalization?.count ?? 0, onClick: () => openModal("Finalization of Design",      dashData?.designFinalization?.data || [], "standard") },
    ];

    return (
        <Paper className={classes.pageWrapper}>
            <div className={classes.headerBanner}>
                <Typography className={classes.title}>
                    <CarryOutOutlined className={classes.titleIcon} />
                    My Commitments Dashboard
                </Typography>
                <Typography className={classes.subtitle}>
                    <CalendarOutlined />
                    {moment().format("MMMM YYYY")} — click any card to view details
                </Typography>
            </div>

            {/* ── Cards ─────────────────────────────────────────────────────── */}
            <div className={classes.cardsWrapper}>
                {topRowCards.map(card => <CommitmentCard key={card.cardKey} classes={classes} {...card} loading={cardsLoading} />)}
            </div>
            <div className={`${classes.cardsWrapper} ${classes.cardsWrapperSecondRow}`}>
                {stageRowCards.map(card => <CommitmentCard key={card.cardKey} classes={classes} {...card} loading={cardsLoading} />)}
            </div>

            {/* ── Design Project Dashboard ───────────────────────────────────── */}
            <Paper elevation={2} style={{ marginTop: "40px" }}>
                <div style={{ padding: "16px" }}>
                    <Typography variant="h5" style={{ margin: "16px 5px 32px", textAlign: "center", fontWeight: "bold", textDecoration: "underline" }}>
                        Design Project Dashboard
                    </Typography>

                    <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                        <FormControl variant="outlined" style={{ flex: "1 1 180px", minWidth: 160 }}>
                            <InputLabel id="status-label">Select Status</InputLabel>
                            <Select labelId="status-label" value={designFilters.status} label="Select Status"
                                onChange={e => handleFilterChange("status", e.target.value)}>
                                <MenuItem value="Active + IDSO Marked Completed">Active + IDSO Marked Completed</MenuItem>
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Design">Design</MenuItem>
                                <MenuItem value="On-Hold">On-Hold</MenuItem>
                                <MenuItem value="IDSO Marked as Completed">IDSO Marked as Completed</MenuItem>
                                <MenuItem value="KOM Pending">KOM Pending</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl variant="outlined" style={{ flex: "1 1 160px", minWidth: 140 }}>
                            <InputLabel id="ec-label">Experience Center</InputLabel>
                            <Select labelId="ec-label" value={designFilters.experienceCenter} label="Experience Center"
                                onChange={e => handleFilterChange("experienceCenter", e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                {accessibleExpCenters.map(ec => (
                                    <MenuItem key={ec._id} value={ec._id}>{ec.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl variant="outlined" style={{ flex: "1 1 120px", minWidth: 110 }}>
                            <InputLabel id="city-label">City</InputLabel>
                            <Select labelId="city-label" value={designFilters.city} label="City"
                                onChange={e => handleFilterChange("city", e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                {availableCities.map(c => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField label="Sales Won Start Date" type="date" variant="outlined"
                            style={{ flex: "1 1 160px", minWidth: 150 }}
                            value={designFilters.startDate} InputLabelProps={{ shrink: true }}
                            inputProps={{ max: new Date().toISOString().split("T")[0] }}
                            onChange={e => handleFilterChange("startDate", e.target.value)} />

                        <TextField label="Sales Won End Date" type="date" variant="outlined"
                            style={{ flex: "1 1 160px", minWidth: 150 }}
                            value={designFilters.endDate} InputLabelProps={{ shrink: true }}
                            onChange={e => handleFilterChange("endDate", e.target.value)} />

                        <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                            <Button variant="contained" className={classes.submitButton} onClick={applyFilters}>Submit</Button>
                            <Button variant="contained" className={classes.clearButton}  onClick={clearFilters}>Clear</Button>
                        </div>
                    </div>
                </div>

                <TableContainer>
                    <Table style={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={15}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", width: "100%" }}>
                                        <Box style={{ textAlign: "left" }}>
                                            <b>Design Quotation Value is less Sales Quotation Value.</b>
                                            <Box style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                                                {[{ percent: "11-20%", color: "yellow" }, { percent: ">20%", color: "red" }].map((ele, i) => (
                                                    <Box key={i} style={{ borderRadius: "7px", width: "70px", height: "20px", backgroundColor: ele.color, textAlign: "center", lineHeight: "20px" }}>
                                                        <b>{ele.percent}</b>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                        <TablePagination
                                            rowsPerPageOptions={[50, 100, 200, 500]}
                                            component="div"
                                            count={pagination.total}
                                            rowsPerPage={pagination.limit}
                                            page={pagination.page}
                                            onPageChange={handlePageChange}
                                            onRowsPerPageChange={handleRowsPerPageChange}
                                            SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
                                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                {["Sr. No","Project No","Lead No.","Client Name","City","Experience Center","Sales Quotation Value (Lacs)","Sales Won Date","Status","Design Start Date","Ageing","Commitment Date","Design Value (Lacs)","Last Communication Date","Last Communication Message"].map(h => (
                                    <TableCell key={h} className={classes.dashHeaderCell}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dashboardProjects.length > 0 ? (
                                dashboardProjects.map((project, idx) => (
                                    <TableRow key={project.id || idx}>
                                        <TableCell className={classes.dashTableCell}>{project.srNo || idx + 1}</TableCell>
                                        <TableCell className={classes.dashTableCell}>
                                            <Link to={`/project-logs/${project.id}`} target="_blank" rel="noopener noreferrer"
                                                style={{ textDecoration: "underline", color: "#1C375D", fontWeight: 700 }}>
                                                {project.projectNo || "N/A"}
                                            </Link>
                                        </TableCell>
                                        <TableCell className={classes.dashTableCell}>
                                            <Link to={`/project-logs/${project.id}`} target="_blank" rel="noopener noreferrer"
                                                style={{ textDecoration: "underline", color: "#1C375D", fontWeight: 700 }}>
                                                {project.leadNo || "N/A"}
                                            </Link>
                                        </TableCell>
                                        <TableCell className={classes.dashTableCell}>{project.clientName || "N/A"}</TableCell>
                                        <TableCell className={classes.dashTableCell}>{project.cityName || "N/A"}</TableCell>
                                        <TableCell className={classes.dashTableCell}>{project.experienceCenterName || "N/A"}</TableCell>
                                        <TableCell className={classes.dashTableCell}>
                                            {project.salesQuotationValue ? (project.salesQuotationValue / 100000).toFixed(2) : "N/A"}
                                        </TableCell>
                                        <TableCell className={classes.dashTableCell}>{project.salesWonDate || "N/A"}</TableCell>
                                        <TableCell className={classes.dashTableCell}>{project.status || "N/A"}</TableCell>
                                        <TableCell className={classes.dashTableCell}>{project.designStartDate || "N/A"}</TableCell>
                                        <TableCell className={classes.dashTableCell}>{project.ageing ?? "N/A"}</TableCell>
                                        <TableCell className={classes.dashTableCell}>{project.tentativeDesignSignOffDate || "N/A"}</TableCell>
                                        <TableCell style={{ backgroundColor: project.designValueStatus === "yellow" ? "yellow" : project.designValueStatus === "red" ? "red" : undefined }} className={classes.dashTableCell}>
                                            {project.designValue ? (project.designValue / 100000).toFixed(2) : "N/A"}
                                        </TableCell>
                                        <TableCell className={classes.dashTableCell}>
                                            {project.lastCommunicationDate ? moment(project.lastCommunicationDate).format("DD-MM-YYYY") : "N/A"}
                                        </TableCell>
                                        <TableCell className={classes.dashTableCell}>
                                            <InfoIcon style={{ cursor: "pointer", color: "#666" }} onClick={() => { setNotesLeadData(project); setShowLatestNotes(true); }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={15} align="center" className={classes.dashTableCell}>No design projects found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* ── Detail Modal ───────────────────────────────────────────────── */}
            <Dialog open={modal.open} onClose={closeModal} maxWidth="xl" fullWidth
                PaperProps={{ style: { borderRadius: "10px", maxHeight: "90vh" } }}>
                <DialogTitle disableTypography style={{ backgroundColor: "#1C375D", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px" }}>
                    <Typography style={{ fontWeight: 700, fontSize: "16px", color: "#fff" }}>
                        {modal.title}
                        <span style={{ marginLeft: 12, fontSize: "13px", fontWeight: 400, opacity: 0.8 }}>
                            ({rows.length} records)
                        </span>
                    </Typography>
                    <IconButton size="small" onClick={closeModal} style={{ color: "#fff" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ padding: "16px 20px" }}>
                    {rows.length > 0 && (
                        <div className={classes.csv}>
                            <CSVLink data={rows} headers={csvHeaders}
                                filename={`${modal.title.replace(/\s+/g, "_")}_${moment().format("YYYY-MM-DD")}.csv`}
                                style={{ textDecoration: "none" }}>
                                <button style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#355025", color: "#FFF2EB", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                                    <DownloadOutlined /> Download CSV
                                </button>
                            </CSVLink>
                        </div>
                    )}
                    {rows.length === 0 ? (
                        <Typography className={classes.noData}>No data found</Typography>
                    ) : (
                        <TableContainer style={{ maxHeight: "65vh", overflowY: "auto" }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {columns.map(col => (
                                            <TableCell key={col.key} className={classes.headerCell}>{col.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, idx) => (
                                        <TableRow key={idx} className={idx % 2 === 0 ? classes.tableRowEven : classes.tableRowOdd}>
                                            {columns.map(col => (
                                                <TableCell key={col.key} className={classes.bodyCell}>
                                                    {(col.key === "lead_no" || col.key === "erpProjectNo") && row.leadId ? (
                                                        <a href={`/project-logs/${row.leadId}`} target="_blank" rel="noopener noreferrer"
                                                            style={{ color: "#1C375D", textDecoration: "underline", fontWeight: 600 }}>
                                                            {row[col.key]}
                                                        </a>
                                                    ) : row[col.key]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Latest Notes Modal ─────────────────────────────────────────── */}
            <Dialog open={showLatestNotes} onClose={() => setShowLatestNotes(false)} fullWidth maxWidth="sm">
                <DialogTitle>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>Latest Notes</span>
                        <span style={{ fontSize: "1rem", color: "#666" }}>{notesLeadData?.leadNo || ""}</span>
                    </div>
                </DialogTitle>
                <Divider />
                <DialogContent style={{ marginBottom: "18px" }}>
                    {notesLeadData?.lastCommunicationMessage || "No Notes"}
                </DialogContent>
            </Dialog>
        </Paper>
    );
}
